import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Auth0Provider } from '@auth0/auth0-react';
import { auth0Config } from '@/config/auth0.config';

// Critical pages loaded immediately (login, auth, homepage)
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import EnhancedLanding from "@/pages/EnhancedLanding";
import CreatorHub from "@/pages/CreatorHub";
import AboutPage from "@/pages/AboutPage";
import FeaturesPage from "@/pages/FeaturesPage";
import PricingPage from "@/pages/PricingPage";

// Lazy-loaded pages for code splitting
const SetupPage = lazy(() => import("@/pages/SetupPage"));
const OMDashboard = lazy(() => import("@/pages/OMDashboard"));
const OpenClawInfo = lazy(() => import("@/pages/OpenClawInfo"));
const OpenClawControlUI = lazy(() => import("@/pages/OpenClawControlUI"));
const OpenClawChat = lazy(() => import("@/pages/OpenClawChat"));
const OpenClawLogs = lazy(() => import("@/pages/OpenClawLogs"));
const OpenClawAutonomous = lazy(() => import("@/pages/OpenClawAutonomous"));
const OpenClawDashboard = lazy(() => import("@/pages/OpenClawDashboard"));
const OpenClawTUI = lazy(() => import("@/pages/OpenClawTUI"));
const OpenClawSettings = lazy(() => import("@/pages/OpenClawSettings"));
const HybridIntegrationsHub = lazy(() => import("@/pages/HybridIntegrationsHub"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const OnboardingFunnelDashboard = lazy(() => import("@/pages/OnboardingFunnelDashboard"));
const DripComposer = lazy(() => import("@/pages/DripComposer"));
const AdminOverview = lazy(() => import("@/pages/AdminOverview"));
const KeySetup = lazy(() => import("@/pages/KeySetup"));
const GithubRepos = lazy(() => import("@/pages/GithubRepos"));
const HybridSkills = lazy(() => import("@/pages/HybridSkills"));
const HybridSchedules = lazy(() => import("@/pages/HybridSchedules"));
const PlatformIntel = lazy(() => import("@/pages/PlatformIntel"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const IntegrationsHub = lazy(() => import("@/pages/IntegrationsHub"));
const LiveBuildDashboard = lazy(() => import("@/pages/LiveBuildDashboard"));
const TemplatesMarketplace = lazy(() => import("@/pages/TemplatesMarketplace"));
const MCPManagement = lazy(() => import("@/pages/MCPManagement"));
const DataHubDashboard = lazy(() => import("@/pages/DataHub/DataHubDashboard"));
const SMSDashboard = lazy(() => import("@/pages/SMSDashboard"));
const EmailClient = lazy(() => import("@/pages/EmailClient"));
const PortkeyGateway = lazy(() => import("@/pages/PortkeyGateway"));
const AIToolsHub = lazy(() => import("@/pages/AIToolsHub"));
const AIToolsAdmin = lazy(() => import("@/pages/AIToolsAdmin"));
const ZapierIntegration = lazy(() => import("@/pages/ZapierIntegration"));
const GitHubRepositories = lazy(() => import("@/pages/GitHubRepositories"));
const PackageUpdateDashboard = lazy(() => import("@/pages/PackageUpdateDashboard"));
const GoogleCloudAI = lazy(() => import("@/pages/GoogleCloudAI"));
const GoogleCloudStudio = lazy(() => import("@/pages/GoogleCloudStudio"));
const GeminiStudio = lazy(() => import("@/pages/GeminiStudio"));
const EnhancedStorageManager = lazy(() => import("@/pages/EnhancedStorageManager"));
const AnalyticsHub = lazy(() => import("@/pages/Analytics/AnalyticsHub"));
const AllIntegrationsHub = lazy(() => import("@/pages/AllIntegrationsHub"));
const TerraForgeStudio = lazy(() => import("@/pages/TerraForge/TerraForgeStudio"));
const FigAIDashboard = lazy(() => import("@/pages/FigAIDashboard"));
const GenesisDashboard = lazy(() => import("@/pages/GenesisDashboard"));
const CreationStudio = lazy(() => import("@/pages/CreationStudio"));
const NexusHubDashboard = lazy(() => import("@/pages/NexusHubDashboard"));
const SecretsVault = lazy(() => import("@/pages/SecretsVault"));
const HerculesDashboard = lazy(() => import("@/pages/HerculesDashboard"));
const AutomationsDashboard = lazy(() => import("@/pages/AutomationsDashboard"));
const APIDocumentation = lazy(() => import("@/pages/APIDocumentation"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const PaymentAdminDashboard = lazy(() => import("@/pages/PaymentAdminDashboard"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const SelfHostingDashboard = lazy(() => import("@/pages/SelfHostingDashboard"));
const UsersDashboard = lazy(() => import("@/pages/UsersDashboard"));
const VendorsDashboard = lazy(() => import("@/pages/VendorsDashboard"));
const Auctions = lazy(() => import("@/pages/Auctions"));
const AIOSandbox = lazy(() => import("@/pages/AIOSandbox"));
const WorkflowsPage = lazy(() => import("@/pages/WorkflowsPage"));
const SocialNetwork = lazy(() => import("@/pages/SocialNetwork"));
const AutonomousDashboard = lazy(() => import("@/pages/AutonomousDashboard"));
const AgentSkillsManager = lazy(() => import("@/pages/AgentSkillsManager"));
const MCPDashboard = lazy(() => import("@/pages/MCPDashboard"));
const AICreativeStudio = lazy(() => import("@/pages/AICreativeStudio"));
const AITrainingStudio = lazy(() => import("@/pages/AITrainingStudio"));
const ResearchPaperExplorer = lazy(() => import("@/pages/ResearchPaperExplorer"));
const AutonomousResearchDashboard = lazy(() => import("@/pages/AutonomousResearchDashboard"));
const NextGenAIStudio = lazy(() => import("@/pages/NextGenAIStudio"));
const TrustPage = lazy(() => import("@/pages/TrustPage"));

import { Toaster } from "@/components/ui/sonner";
import { NotificationProvider } from "@/components/NotificationSystem";
import { initPostHog, trackPageView } from "@/lib/posthog";
import OnboardingModal from "@/components/OnboardingModal";

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id - MUST be synchronous
  // This runs BEFORE ProtectedRoute to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<EnhancedLanding />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/integrations/zapier" element={<ZapierIntegration />} />
        <Route path="/creator-hub" element={<CreatorHub />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/onboarding-funnel" element={<OnboardingFunnelDashboard />} />
        <Route path="/admin/drip-composer" element={<DripComposer />} />
        <Route path="/admin/overview" element={<AdminOverview />} />
        <Route path="/admin/key-setup" element={<KeySetup />} />
        <Route path="/admin/github-repos" element={<GithubRepos />} />
        <Route path="/admin/hybrid-skills" element={<HybridSkills />} />
        <Route path="/admin/hybrid-schedules" element={<HybridSchedules />} />
        <Route path="/admin/platform-intel" element={<PlatformIntel />} />
        <Route path="/account/change-password" element={<ChangePassword />} />
        <Route path="/admin/openclaw-setup" element={<SetupPage />} />
        <Route path="/integrations" element={<IntegrationsHub />} />
        <Route path="/all-integrations" element={<AllIntegrationsHub />} />
        <Route path="/ai-cofounder" element={<LiveBuildDashboard />} />
        <Route path="/templates" element={<TemplatesMarketplace />} />
        <Route path="/mcp" element={<MCPManagement />} />
        <Route path="/data-hub" element={<DataHubDashboard />} />
        <Route path="/sms" element={<SMSDashboard />} />
        <Route path="/admin/email" element={<EmailClient />} />
        <Route path="/dashboard" element={<OpenClawDashboard />} />
        <Route path="/maintenance" element={<OMDashboard />} />
        <Route path="/creator-hub" element={<CreatorHub />} />
        <Route path="/hybrid-ai" element={<HybridIntegrationsHub />} />
        <Route path="/openclaw-info" element={<OpenClawInfo />} />
        <Route path="/openclaw-control" element={<OpenClawControlUI />} />
        <Route path="/openclaw-chat" element={<OpenClawChat />} />
        <Route path="/openclaw-logs" element={<OpenClawLogs />} />
        <Route path="/openclaw-autonomous" element={<OpenClawAutonomous />} />
        <Route path="/openclaw-tui" element={<OpenClawTUI />} />
        <Route path="/openclaw-settings" element={<OpenClawSettings />} />
        <Route path="/portkey" element={<PortkeyGateway />} />
        <Route path="/ai-tools/admin" element={<AIToolsAdmin />} />
        <Route path="/google-cloud-ai" element={<GoogleCloudAI />} />
        <Route path="/google-cloud-studio" element={<GoogleCloudStudio />} />
        <Route path="/gemini-studio" element={<GeminiStudio />} />
        <Route path="/storage" element={<EnhancedStorageManager />} />
        <Route path="/analytics" element={<AnalyticsHub />} />
        <Route path="/terraforge-studio" element={<TerraForgeStudio />} />
        <Route path="/terraforge" element={<Navigate to="/terraforge-studio" replace />} />
        <Route path="/fig-ai" element={<FigAIDashboard />} />
        <Route path="/genesis" element={<GenesisDashboard />} />
        <Route path="/creation-studio" element={<CreationStudio />} />
        <Route path="/hybrid-integrations" element={<NexusHubDashboard />} />
        <Route path="/secrets-vault" element={<SecretsVault />} />
        <Route path="/hercules" element={<HerculesDashboard />} />
        <Route path="/automations" element={<AutomationsDashboard />} />
        <Route path="/api-docs" element={<APIDocumentation />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/admin/payments" element={<PaymentAdminDashboard />} />
        <Route path="/selfhosting" element={<SelfHostingDashboard />} />

        <Route path="/ai-tools" element={<AIToolsHub />} />
        <Route path="/github-repos" element={<GitHubRepositories />} />
        <Route path="/package-updates" element={<PackageUpdateDashboard />} />
        
        {/* Marketplace Core Features */}
        <Route path="/users" element={<UsersDashboard />} />
        <Route path="/vendors" element={<VendorsDashboard />} />
        <Route path="/auctions" element={<Auctions />} />
        
        {/* AIO Sandbox */}
        <Route path="/aio-sandbox" element={<AIOSandbox />} />
        
        {/* Autonomous Workflows */}
        <Route path="/workflows" element={<WorkflowsPage />} />
        
        {/* Social Network */}
        <Route path="/social" element={<SocialNetwork />} />
        
        {/* Autonomous Dashboard */}
        <Route path="/autonomous" element={<AutonomousDashboard />} />
        
        {/* Agent Skills Manager */}
        <Route path="/agent-skills" element={<AgentSkillsManager />} />
        
        {/* MCP Dashboard - Consolidated Route */}
        <Route path="/mcp" element={<MCPDashboard />} />
        <Route path="/mcp-dashboard" element={<MCPDashboard />} />
        
        {/* AI Creative Studio */}
        <Route path="/ai-creative-studio" element={<AICreativeStudio />} />
        
        {/* AI Training Studio (TREX) */}
        <Route path="/ai-training-studio" element={<AITrainingStudio />} />
        
        {/* Research Paper Explorer */}
        <Route path="/research-papers" element={<ResearchPaperExplorer />} />
        
        {/* Autonomous Research Dashboard */}
        <Route path="/autonomous-research" element={<AutonomousResearchDashboard />} />

        {/* Next-Gen AI Studio (Claude 4.7 / Gemma 4 / MicroVM Sandbox) */}
        <Route path="/next-gen-ai" element={<NextGenAIStudio />} />

        {/* Platform Trust / live audit page */}
        <Route path="/trust" element={<TrustPage />} />
        
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    // Initialize analytics on app load
    initPostHog();
    trackPageView('App Loaded');
  }, []);

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: auth0Config.redirectUri,
        audience: auth0Config.audience,
        scope: auth0Config.scope
      }}
    >
      <ErrorBoundary>
        <NotificationProvider>
          <div className="App dark">
            <Toaster data-testid="global-toaster" richColors position="top-center" />
            <BrowserRouter>
              <AppRouter />
              <OnboardingModal />
            </BrowserRouter>
          </div>
        </NotificationProvider>
      </ErrorBoundary>
    </Auth0Provider>
  );
}

export default App;
